import { GlassWater, Sparkles, CalendarHeart, Building2, Church, GraduationCap, Hotel, PartyPopper, Check } from "lucide-react";
import { IMAGES, CONTACT, SEO_PAGES } from "@/data/site";
import { PageHero, Eyebrow, SectionHeading, ConversionRow } from "@/components/Shared";
import { Seo } from "@/components/Seo";
import { TrustBadges } from "@/components/CertificateProvider";

const PACKAGES = [
  {
    name: "Essential Bar",
    tagline: "Intimate gatherings",
    features: ["Up to 50 guests", "3 signature drinks", "Styled drink station", "2 hours service"],
  },
  {
    name: "Premium Bar",
    tagline: "Most popular",
    features: ["Up to 150 guests", "5 drinks + smoothies", "Branded glassware", "4 hours service", "Dedicated attendants"],
    highlight: true,
  },
  {
    name: "Signature Experience",
    tagline: "Large & corporate events",
    features: ["150+ guests", "Full custom menu", "Premium styling & décor", "Full-day service", "On-site team"],
  },
];

const STEPS = [
  { n: "01", title: "Tell us about your event", text: "Date, venue, guest count and the vibe you're going for." },
  { n: "02", title: "We craft your menu", text: "A tailored selection of juices, smoothies and traditional drinks." },
  { n: "03", title: "We set up & serve", text: "Our team handles styling, service and cleanup — you enjoy." },
];

const OCCASIONS = [
  { icon: Building2, label: "Corporate Events" },
  { icon: CalendarHeart, label: "Weddings" },
  { icon: Church, label: "Church Functions" },
  { icon: GraduationCap, label: "School Fairs" },
  { icon: Hotel, label: "Hotels" },
  { icon: PartyPopper, label: "Celebrations" },
];

const Catering = () => {
  return (
    <div data-testid="catering-page">
      <Seo title={SEO_PAGES.catering.title} description={SEO_PAGES.catering.description} />
      <PageHero
        eyebrow="Catering & Events"
        title="A premium healthy drink bar, brought to you"
        intro="Elevate any occasion with a beautifully styled Faf's drink bar — fresh, natural and unforgettable. Fully serviced, anywhere in Ghana."
        image={IMAGES.catering}
        imageAlt="Faf's event drink and snack bar set up at a celebration"
      />

      {/* Occasions */}
      <section className="pb-12">
        <div className="container-px mx-auto max-w-[1400px]">
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
            {OCCASIONS.map((o) => (
              <div key={o.label} className="rounded-2xl bg-brand-alt border border-black/5 p-5 text-center transition-all duration-300 hover:-translate-y-1 hover:shadow-md">
                <o.icon size={26} className="mx-auto text-brand-green" />
                <p className="mt-3 text-sm font-medium text-brand-ink">{o.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Trust badge strip */}
      <section data-testid="catering-trust" className="pb-4">
        <div className="container-px mx-auto max-w-[1400px]">
          <div className="rounded-3xl bg-brand-alt border border-black/5 px-6 py-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <p className="text-sm font-medium text-brand-ink">Trusted & certified for safe food handling</p>
            <TrustBadges variant="light" />
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="section-py bg-brand-alt">
        <div className="container-px mx-auto max-w-[1400px]">
          <SectionHeading eyebrow="How It Works" title="Effortless from enquiry to event" />
          <div className="mt-14 grid gap-6 md:grid-cols-3">
            {STEPS.map((s) => (
              <div key={s.n} className="rounded-3xl bg-white border border-black/5 p-8">
                <span className="text-4xl font-bold text-brand-mist">{s.n}</span>
                <h3 className="mt-3 text-xl font-medium text-brand-ink">{s.title}</h3>
                <p className="mt-2 text-brand-slate leading-relaxed">{s.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Packages */}
      <section className="section-py">
        <div className="container-px mx-auto max-w-[1400px]">
          <SectionHeading align="center" eyebrow="Packages" title="Choose your experience" intro="Flexible packages for every size of celebration. All fully customisable." />
          <div className="mt-14 grid gap-6 lg:grid-cols-3">
            {PACKAGES.map((p, i) => (
              <div
                key={p.name}
                data-testid={`package-${i}`}
                className={`rounded-3xl p-8 border transition-all duration-300 hover:-translate-y-1 ${
                  p.highlight ? "bg-brand-greenDark text-white border-transparent shadow-xl" : "bg-white border-black/5 hover:shadow-lg"
                }`}
              >
                <p className={`eyebrow ${p.highlight ? "text-white/60" : "text-brand-purple"}`}>{p.tagline}</p>
                <h3 className={`mt-2 text-2xl font-semibold ${p.highlight ? "text-white" : "text-brand-ink"}`}>{p.name}</h3>
                <ul className="mt-6 space-y-3">
                  {p.features.map((f) => (
                    <li key={f} className={`flex items-center gap-3 ${p.highlight ? "text-white/85" : "text-brand-ink"}`}>
                      <span className={`grid h-5 w-5 place-items-center rounded-full shrink-0 ${p.highlight ? "bg-white/20 text-white" : "bg-brand-mist text-brand-greenDark"}`}><Check size={12} /></span>
                      <span className="text-sm">{f}</span>
                    </li>
                  ))}
                </ul>
                <a
                  href={CONTACT.whatsappCateringHref}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`package-cta-${i}`}
                  className={`mt-8 block rounded-full px-6 py-3.5 text-center text-sm font-medium transition-all duration-300 ${
                    p.highlight ? "bg-white text-brand-greenDark hover:-translate-y-0.5" : "bg-brand-green text-white hover:bg-brand-greenDark"
                  }`}
                >
                  Request a Quote
                </a>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA banner */}
      <section className="pb-20 lg:pb-32">
        <div className="container-px mx-auto max-w-[1400px]">
          <div className="grid lg:grid-cols-2 gap-10 items-center rounded-[2.5rem] bg-brand-alt border border-black/5 overflow-hidden">
            <div className="img-zoom-wrap relative overflow-hidden aspect-[5/4] lg:aspect-auto lg:h-full min-h-[300px]">
              <img src={IMAGES.cateringPour} alt="Pouring fresh drinks" className="img-zoom h-full w-full object-cover" />
            </div>
            <div className="p-10 lg:p-14">
              <Eyebrow>Let's Plan Together</Eyebrow>
              <h2 className="mt-4 text-3xl sm:text-4xl font-semibold tracking-tight text-brand-ink">
                Book your Faf's drink bar today
              </h2>
              <p className="mt-4 text-brand-slate leading-relaxed">
                Share your date and we'll get back to you with a tailored
                proposal within 24 hours.
              </p>
              <div className="mt-8"><ConversionRow testidPrefix="catering-cta" variant="catering" /></div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Catering;
